import { motion } from "framer-motion";
import { AlertTriangle, ChevronDown, Zap } from "lucide-react";
import { SurvivalCountdown } from "./SurvivalCountdown";
import { MeetSamIntro } from "./MeetSamIntro";
import { SamVideoDiaries } from "./SamVideoDiaries";
import { sound } from "./sound";

const rise = (delay) => ({
  initial: { y: 36, opacity: 0 },
  animate: { y: 0, opacity: 1 },
  transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = ({ onOpenBid }) => (
  <section data-testid="story-below-fold" className="mx-auto max-w-6xl px-4 pt-6 sm:px-6">
    <div className="panel corner-tick relative grid gap-8 overflow-visible p-4 sm:grid-cols-2 sm:items-center sm:p-8">
      <div>
        <motion.div {...rise(0)}>
          <span
            data-testid="critical-status-badge"
            className="mono inline-flex items-center gap-2 border border-red-500/60 bg-red-500/10 px-3 py-1.5 text-[10px] tracking-[0.3em] text-red-400"
          >
            <AlertTriangle size={12} className="blink" /> CRITICAL STATUS
          </span>
        </motion.div>

        <div className="mt-5 overflow-hidden">
          <motion.h1
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="text-5xl font-bold tracking-tight text-white sm:text-6xl"
            data-testid="hero-headline"
          >
            Meet Sam.
          </motion.h1>
        </div>

        <motion.p {...rise(0.25)} className="mt-5 max-w-md text-sm leading-relaxed text-slate-400 sm:text-base text-justify">
          42 hours ago, Commander Sam's primary thrusters went dark in Sector 7.{" "}
          <span className="relative inline-block">
            <span className="relative z-10 px-2">He's alive</span>
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 120 40" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg">
              <ellipse cx="60" cy="20" rx="58" ry="18" stroke="#ff8c46" strokeWidth="1.5" fill="none" opacity="0.85" />
            </svg>
          </span>
          , but <strong className="text-gmh-orange">Expedition 1</strong> has only{" "}
          <strong className="text-gmh-orange">60 days of rations</strong> left. If this window ends
          without $10,000 of fuel, the beacon goes dark — a later expedition can still start.{" "}
          <strong className="text-gmh-orange">$10k is the only win</strong>.{" "}
          <span className="relative inline-block">
            <span className="relative z-10">Claim your spot on the board</span>
            <svg className="absolute -bottom-1 left-0 w-full h-2" viewBox="0 0 200 8" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 4 Q50 3 100 4 Q150 5 199 4" stroke="#ff8c46" strokeWidth="2" strokeLinecap="round" fill="none" />
            </svg>
          </span>
          {" "}
          — a ranked listing, not a rescue — and send the fuel that moves him closer to Earth.
        </motion.p>

        <motion.div {...rise(0.35)} className="mt-5">
          <button
            data-testid="hero-send-fuel-btn"
            onClick={() => { sound.blip(); onOpenBid(); }}
            className="cta-fuel mono flex w-full items-center justify-center gap-2 py-3.5 text-xs font-bold tracking-[0.3em] text-[#2a1000]"
          >
            <Zap size={14} /> SEND HIM FUEL
          </button>
        </motion.div>
      </div>

      <div className="relative min-w-0">
        <MeetSamIntro />
        <SurvivalCountdown variant="story" className="mt-3" />
      </div>
    </div>

    <div className="mt-10 flex flex-col items-center text-center">
      <ChevronDown
        data-testid="story-hook-chevron"
        size={36}
        className="mb-2 text-gmh-orange blink"
        aria-hidden="true"
      />
      <p
        data-testid="story-hook-line"
        className="text-3xl font-bold tracking-[0.12em] text-gmh-orange sm:text-5xl"
      >
        WILL SAM MAKE IT HOME?
      </p>
      <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-500">
        Pay to rank your site on the board. Every dollar is fuel. Cheaper than buying the same
        eyes with ads.
      </p>
    </div>

    <SamVideoDiaries />
  </section>
);
